/**
 * Helpers for turning a downloaded blob into a file the browser saves.
 */

/**
 * Trigger a browser download for a blob.
 *
 * The anchor is appended to the document before `click()` because Firefox and
 * Safari ignore clicks on elements that are not in the DOM, and the object URL
 * is revoked afterwards so the blob can be garbage collected.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Read a blob as text, falling back to FileReader where `Blob.text` is missing
 * (older Safari, and jsdom under test).
 */
function blobToText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Pull a human-readable message out of a failed blob request.
 *
 * When a request uses `responseType: 'blob'`, axios hands back the *error* body
 * as a Blob too, so `error.response.data.message` is always undefined. This reads
 * the blob back as text and unwraps the API's `{ success, message }` envelope.
 */
export async function readBlobError(error: any, fallback: string): Promise<string> {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await blobToText(data);
      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || fallback;
      } catch {
        // Not JSON - use the raw body if it looks like a message, not markup.
        return text && text.length < 200 ? text : fallback;
      }
    } catch {
      return fallback;
    }
  }

  return data?.message || data?.error || error?.message || fallback;
}
